import type { ComponentType } from "react"

/**
 * Figma-exact outline icons for the Soulmate quiz, extracted 1:1 from the design
 * file (Live-Palmistry-4.0). Each path uses `currentColor` so the colour is
 * driven by the row's `text-*` class, and a 32-viewBox with stroke-width 2 — at
 * `size-8` this renders the same ~2px stroke as the lucide rows around them.
 *
 * `strokeWidth` is accepted for interface-compatibility with the lucide icons in
 * the shared map but intentionally ignored: the design's weight is baked in.
 */
export interface SoulmateIconProps {
  className?: string
  strokeWidth?: number
}

export type SoulmateIcon = ComponentType<SoulmateIconProps>

function IconSvg({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <svg
      className={className}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  )
}

export function HeartIcon({ className }: SoulmateIconProps) {
  return (
    <IconSvg className={className}>
      <path d="M13.8795 26.6234C10.1178 23.8105 2.66528 17.3796 2.66528 11.5924C2.66528 7.76734 5.4723 4.6665 9.33195 4.6665C11.332 4.6665 13.332 5.33317 15.9986 7.99984C18.6653 5.33317 20.6653 4.6665 22.6653 4.6665C26.5249 4.6665 29.332 7.76734 29.332 11.5924C29.332 17.3796 21.8794 23.8105 18.1177 26.6234C16.8518 27.57 15.1454 27.57 13.8795 26.6234Z" />
    </IconSvg>
  )
}

export function SparklesIcon({ className }: SoulmateIconProps) {
  return (
    <IconSvg className={className}>
      <path d="M20.0001 2.6665C21.2587 8.25436 23.4387 10.672 29.3334 11.9998C23.7455 13.2586 21.3279 15.4385 20.0001 21.3332C18.7414 15.7453 16.5614 13.3277 10.6667 11.9998C16.5614 10.672 18.7414 8.25436 20.0001 2.6665Z" />
      <path d="M9.33341 16C10.2819 20.2105 12.0088 21.7676 16.0001 22.6667C11.7896 23.6151 10.2325 25.342 9.33341 29.3333C8.38496 25.1228 6.65807 23.5657 2.66675 22.6667C6.87723 21.7183 8.43432 19.9913 9.33341 16Z" />
    </IconSvg>
  )
}

export function WaveIcon({ className }: SoulmateIconProps) {
  return (
    <IconSvg className={className}>
      <path d="M5 20.5145L5.81069 21.5485C6.50509 22.4341 7.78594 22.5891 8.67154 21.8947C8.9354 21.6878 9.14413 21.419 9.27922 21.1121L9.84848 19.8189C10.2784 18.8421 11.4188 18.3989 12.3955 18.8288C12.878 19.0412 13.2528 19.4416 13.4329 19.937L13.7517 20.8135C14.1363 21.8711 15.3054 22.4167 16.363 22.0321C16.9295 21.8261 17.3757 21.3799 17.5817 20.8135L17.9004 19.937C18.2651 18.9341 19.3738 18.4167 20.3767 18.7814C20.8721 18.9616 21.2725 19.3364 21.4848 19.8189L22.0541 21.1121C22.5075 22.1421 23.7101 22.6095 24.7401 22.1561C25.0469 22.021 25.3157 21.8123 25.5226 21.5485L26.3333 20.5145" />
      <path d="M5 10.85L5.81069 11.8839C6.50509 12.7695 7.78594 12.9245 8.67154 12.2301C8.9354 12.0232 9.14413 11.7544 9.27922 11.4475L9.84848 10.1543C10.2784 9.17759 11.4188 8.73434 12.3955 9.16429C12.878 9.37668 13.2528 9.77701 13.4329 10.2725L13.7517 11.1489C14.1363 12.2066 15.3054 12.7522 16.363 12.3676C16.9295 12.1616 17.3757 11.7154 17.5817 11.1489L17.9004 10.2725C18.2651 9.26955 19.3738 8.75216 20.3767 9.11686C20.8721 9.29703 21.2725 9.6718 21.4848 10.1543L22.0541 11.4475C22.5075 12.4775 23.7101 12.945 24.7401 12.4916C25.0469 12.3565 25.3157 12.1478 25.5226 11.8839L26.3333 10.85" />
    </IconSvg>
  )
}

export function BrainIcon({ className }: SoulmateIconProps) {
  return (
    <IconSvg className={className}>
      <path d="M9.33341 5.99984C7.12428 5.99984 5.33341 7.7907 5.33341 9.99984C5.33341 10.7581 5.54437 11.467 5.9108 12.0712C4.06311 12.4246 2.66675 14.0492 2.66675 15.9998C2.66675 17.9505 4.06311 19.575 5.9108 19.9285M8.00008 18.2274C7.12284 18.5374 6.38605 19.1449 5.9108 19.9285C5.54437 20.5326 5.33341 21.2416 5.33341 21.9998C5.33341 24.209 7.12428 25.9998 9.33341 25.9998C9.33341 27.8408 10.8258 29.3332 12.6667 29.3332C14.5077 29.3332 16.0001 27.8408 16.0001 25.9998V5.99984C16.0001 4.15889 14.5077 2.6665 12.6667 2.6665C10.8258 2.6665 9.33341 4.15889 9.33341 5.99984M9.33341 5.99984C9.33341 7.09038 9.85712 8.05861 10.6667 8.66674" />
      <path d="M22.6667 25.9999C24.8759 25.9999 26.6667 24.2089 26.6667 21.9999C26.6667 21.2416 26.4557 20.5327 26.0893 19.9285C27.9369 19.5751 29.3333 17.9505 29.3333 15.9999C29.3333 14.0492 27.9369 12.4246 26.0893 12.0712M22.6667 25.9999C22.6667 27.8408 21.1743 29.3332 19.3333 29.3332C17.4924 29.3332 16 27.8408 16 25.9999V5.99984C16 4.15889 17.4924 2.6665 19.3333 2.6665C21.1743 2.6665 22.6667 4.15889 22.6667 5.99984C24.8759 5.99984 26.6667 7.79069 26.6667 9.99984C26.6667 10.7581 26.4557 11.467 26.0893 12.0712M22.6667 25.9999C22.6667 24.9093 22.1429 23.9411 21.3333 23.3329M26.0893 12.0712C25.614 12.8548 24.8772 13.4621 24 13.7723" />
    </IconSvg>
  )
}
